/**
 * v0.5① 日報機能 増分1 — P-2/P-3/P-4 (日報を開く・値入力・添付/OCR確認) のAPI型・fetchラッパー。
 * バックエンド: apps/api/src/routes/daily-reports.ts。設計: docs/design/v05-daily-report-detail.md §8/§14。
 */
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from './api';

export type DailyReportStatus = 'draft' | 'submitted' | 'approved' | 'rejected';
export type DailyReportReconciliationStatus = 'not_evaluated' | 'not_applicable' | 'ok' | 'ng' | 'ng_accepted';
export type DailyReportFieldVisibility = 'optional' | 'required' | 'conditional' | 'readonly';
export type DailyReportDisplayLevel = 'none' | 'exists_only' | 'full';
export type DailyReportValueKind = 'text' | 'number' | 'money' | 'select' | 'check' | 'computed' | 'line_items' | 'attachment';

export type DailyReportFieldValue = string | number | boolean | null;

export type DailyReportCarryover = { value: DailyReportFieldValue; source_business_date: string; days_ago: number };

export type DailyReportField = {
  field_definition_id: string;
  field_code: string;
  catalog_field_key: string | null;
  label: string;
  value_kind: DailyReportValueKind;
  field_options_json: string | null;
  validation_json: string | null;
  section_label: string | null;
  section_order: number;
  sort_order: number;
  visibility: DailyReportFieldVisibility;
  condition_set_id: string | null;
  display_level: DailyReportDisplayLevel;
  value: DailyReportFieldValue;
  source: 'manual' | 'carryover' | 'ocr' | 'computed' | null;
  is_not_applicable: boolean;
  /** GET /form のみ (§4-1-1)。GET /:id では再現されない (既知の簡略化、詳細はbackendコメント参照)。 */
  carryover_source_field_id?: string | null;
  carryover?: DailyReportCarryover | null;
};

export type DailyReportLineItemRow = {
  line_item_id: string;
  line_no: number;
  denomination_id: string | null;
  unit_id: string | null;
  quantity: number | null;
  face_value: number | null;
  pieces_per_unit: number | null;
  amount: number | null;
  values: Record<string, string | number> | null;
};

export type DailyReportLineItemsGroup = { field_definition_id: string; rows: DailyReportLineItemRow[] };

export type DailyReportOcrView = {
  ocr_result_id: string;
  status: string;
  total_amount: number | null;
  total_count: number | null;
  unreadable_reason: string | null;
  attempt_no: number;
  confirmed_by_user_id: string | null;
  confirmed_at: string | null;
  retake_requested_at: string | null;
  created_at: string;
  /** 2回目以降も読み取れなかった場合に true (§4-2-a 例外脱出導線)。 */
  retake_exhausted: boolean;
};

export type DailyReportAttachment = {
  attachment_id: string;
  field_definition_id: string | null;
  attachment_kind: string;
  file_name?: string;
  mime_type?: string;
  byte_size?: number;
  uploaded_by?: string;
  uploaded_at?: string;
  display_level: 'exists_only' | 'full';
  ocr?: DailyReportOcrView | null;
};

export type DailyReportReconciliation = {
  reconciliation_id: string;
  rule_id: string;
  label: string | null;
  left_value: number | null;
  right_value: number | null;
  difference: number | null;
  verdict: 'ok' | 'ng' | 'not_applicable';
  on_ng: 'block' | 'warn' | null;
  consecutive_same_reason_days?: number | null;
  accepted_by_user_id: string | null;
  accepted_at: string | null;
  accept_reason: string | null;
  accept_reason_id: string | null;
  evaluated_at: string;
};

export type DailyReportHeader = {
  id: string;
  node_id?: string;
  business_date?: string;
  status: DailyReportStatus;
  reconciliation_status: DailyReportReconciliationStatus;
  row_version: number;
  created_by: string;
  created_at?: string;
  updated_at?: string;
  updated_by?: string;
  submitted_at: string | null;
  submitted_by_user_id?: string;
};

export type DailyReportFormResponse = {
  business_date: string;
  default_target: string;
  fell_back_to_current: boolean;
  visibility_context: string;
  daily_report: DailyReportHeader;
  fields: DailyReportField[];
};

export type DailyReportDetailResponse = {
  daily_report: DailyReportHeader;
  fields: DailyReportField[];
  line_items: DailyReportLineItemsGroup[];
  attachments: DailyReportAttachment[];
  reconciliations: DailyReportReconciliation[];
};

export const fetchDailyReportForm = (nodeId: string, businessDate?: string) => {
  const qs = new URLSearchParams({ node_id: nodeId });
  if (businessDate) qs.set('business_date', businessDate);
  return apiFetch<DailyReportFormResponse>(`/daily-reports/form?${qs.toString()}`);
};

export const fetchDailyReportDetail = (id: string) => apiFetch<DailyReportDetailResponse>(`/daily-reports/${id}`);

// =============================================================================
// P-1 日報一覧 (§14-2)
// =============================================================================

export type DailyReportListRow = {
  id: string;
  node_id: string;
  business_date: string;
  status: DailyReportStatus;
  reconciliation_status: DailyReportReconciliationStatus;
  row_version: number;
  created_by: string;
  created_at: string;
  submitted_at: string | null;
  submitted_by_user_id: string | null;
};

export type DailyReportListFilters = {
  node_id?: string;
  from?: string;
  to?: string;
  status?: DailyReportStatus;
  reconciliation_status?: DailyReportReconciliationStatus;
};

export const fetchDailyReports = async (filters: DailyReportListFilters): Promise<DailyReportListRow[]> => {
  const qs = new URLSearchParams();
  if (filters.node_id) qs.set('node_id', filters.node_id);
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  if (filters.status) qs.set('status', filters.status);
  if (filters.reconciliation_status) qs.set('reconciliation_status', filters.reconciliation_status);
  const res = await apiFetch<{ daily_reports: DailyReportListRow[] }>(`/daily-reports?${qs.toString()}`);
  return res.daily_reports;
};

export type DailyReportValuePatchEntry = {
  field_definition_id: string;
  value: DailyReportFieldValue;
  is_not_applicable?: boolean;
  reason?: string;
};

export const patchDailyReportValues = (id: string, rowVersion: number, values: DailyReportValuePatchEntry[]) =>
  apiFetch<{ daily_report_id: string; row_version: number }>(`/daily-reports/${id}/values`, {
    method: 'PATCH', body: JSON.stringify({ row_version: rowVersion, values }),
  });

export type DailyReportLineItemPutRow =
  | { denomination_id: string; unit_id: string; quantity: number }
  | { values: Record<string, string | number> };

export const putDailyReportLineItems = (id: string, fieldDefinitionId: string, rowVersion: number, rows: DailyReportLineItemPutRow[]) =>
  apiFetch<{ daily_report_id: string; field_definition_id: string; row_version: number; row_count: number }>(
    `/daily-reports/${id}/line-items/${fieldDefinitionId}`,
    { method: 'PUT', body: JSON.stringify({ row_version: rowVersion, rows }) },
  );

export type DailyReportSubmitResponse = {
  daily_report_id: string;
  status: DailyReportStatus;
  reconciliation_status: DailyReportReconciliationStatus;
  row_version: number;
  reconciliations: {
    rule_id: string; label: string; left_value: number | null; right_value: number | null;
    difference: number | null; verdict: 'ok' | 'ng' | 'not_applicable'; on_ng: 'block' | 'warn';
  }[];
};

export const submitDailyReport = (id: string, rowVersion: number) =>
  apiFetch<DailyReportSubmitResponse>(`/daily-reports/${id}/submit`, {
    method: 'POST', body: JSON.stringify({ row_version: rowVersion }),
  });

/** 2026-09-09 (user Q1): 提出済みの日報を下書きに差し戻す。company_admin限定 (バックエンドが強制)。 */
export const reopenDailyReport = (id: string, rowVersion: number, reason: string) =>
  apiFetch<{ daily_report_id: string; status: 'draft'; row_version: number }>(`/daily-reports/${id}/reopen`, {
    method: 'POST', body: JSON.stringify({ row_version: rowVersion, reason }),
  });

export type DailyReportAcceptResponse = {
  reconciliation_id: string; accepted_at: string; reconciliation_status: DailyReportReconciliationStatus;
  accept_reason_id: string | null; consecutive_same_reason_days: number;
};

export const acceptDailyReportReconciliation = (id: string, reconciliationId: string, body: { reason?: string; reason_id?: string }) =>
  apiFetch<DailyReportAcceptResponse>(`/daily-reports/${id}/reconciliations/${reconciliationId}/accept`, {
    method: 'POST', body: JSON.stringify(body),
  });

export type DenominationUnit = { unit_id: string; denomination_id: string; unit_code: string; pieces_per_unit: number };
export type Denomination = {
  denomination_id: string; currency_code: string; face_value: number; kind: 'coin' | 'bill';
  sort_order: number; is_active: boolean; units: DenominationUnit[];
};

export const fetchDenominations = () => apiFetch<Denomination[]>('/masters/denominations');

export type DailyReportReconciliationReason = { reason_id: string; code: string; label: string; sort_order: number; is_active: boolean };

export const fetchDailyReportReconciliationReasons = () =>
  apiFetch<DailyReportReconciliationReason[]>('/masters/daily-report-reconciliation-reasons');

export type DailyReportReasonWriteBody = { code?: string; label?: string; sort_order?: number; is_active?: boolean };

export const createDailyReportReconciliationReason = (body: DailyReportReasonWriteBody) =>
  apiFetch<{ reason_id: string }>('/masters/daily-report-reconciliation-reasons', { method: 'POST', body: JSON.stringify(body) });

export const updateDailyReportReconciliationReason = (id: string, body: DailyReportReasonWriteBody) =>
  apiFetch<{ reason_id: string }>(`/masters/daily-report-reconciliation-reasons/${id}`, { method: 'PUT', body: JSON.stringify(body) });

// =============================================================================
// P-5〜P-7 日報設定 (config CRUD、§14-2)
// =============================================================================

export type DailyReportConfigField = {
  field_definition_id: string;
  catalog_field_key: string | null;
  field_code: string;
  label: string | null;
  value_kind: DailyReportValueKind;
  field_options_json: Record<string, unknown> | unknown[] | null;
  validation_json: Record<string, unknown> | null;
  expression_ast_json: Record<string, unknown> | null;
  carryover_source_field_id: string | null;
  visibility_class_id: string | null;
  section_label: string | null;
  section_order: number;
  sort_order: number;
  status: 'active' | 'archived';
  updated_at: string;
};

export const fetchDailyReportConfigFields = () =>
  apiFetch<{ fields: DailyReportConfigField[] }>('/daily-reports/config/fields').then((r) => r.fields);

export type DailyReportFieldUpsertBody = {
  field_definition_id?: string;
  catalog_field_key?: string | null;
  field_code?: string;
  label?: string | null;
  value_kind?: DailyReportValueKind;
  field_options_json?: Record<string, unknown> | unknown[] | null;
  validation_json?: Record<string, unknown> | null;
  expression_ast_json?: Record<string, unknown> | null;
  carryover_source_field_id?: string | null;
  visibility_class_id?: string | null;
  section_label?: string | null;
  section_order?: number;
  sort_order?: number;
  status?: 'active' | 'archived';
};

export const saveDailyReportConfigField = (body: DailyReportFieldUpsertBody) =>
  apiFetch<{ field_definition_id: string }>('/daily-reports/config/fields', { method: 'PUT', body: JSON.stringify(body) });

export type DailyReportNodePolicy = {
  policy_id: string;
  field_definition_id: string;
  node_id: string;
  visibility: DailyReportFieldVisibility | 'hidden' | 'readonly';
  condition_set_id: string | null;
  updated_at: string;
};

export const fetchDailyReportNodePolicies = (nodeId: string) =>
  apiFetch<{ node_policies: DailyReportNodePolicy[] }>(`/daily-reports/config/node-policies?node_id=${encodeURIComponent(nodeId)}`)
    .then((r) => r.node_policies);

export type DailyReportNodePolicyUpsertBody = {
  field_definition_id: string;
  node_id: string;
  visibility: 'hidden' | 'optional' | 'required' | 'conditional' | 'readonly';
  condition_set_id?: string | null;
};

export const saveDailyReportNodePolicy = (body: DailyReportNodePolicyUpsertBody) =>
  apiFetch<{ policy_id: string }>('/daily-reports/config/node-policies', { method: 'PUT', body: JSON.stringify(body) });

export type DailyReportReconciliationRule = {
  rule_id: string;
  node_id: string | null;
  label: string;
  left_ast: Record<string, unknown>;
  right_ast: Record<string, unknown>;
  tolerance: number;
  on_ng: 'block' | 'warn';
  require_reason_on_ng: boolean;
  updated_at: string;
};

export const fetchDailyReportReconciliationRules = (nodeId?: string) =>
  apiFetch<{ reconciliation_rules: DailyReportReconciliationRule[] }>(
    `/daily-reports/config/reconciliation-rules${nodeId ? `?node_id=${encodeURIComponent(nodeId)}` : ''}`,
  ).then((r) => r.reconciliation_rules);

export type DailyReportReconciliationRuleUpsertBody = {
  rule_id?: string;
  node_id?: string | null;
  label?: string;
  left_ast?: Record<string, unknown>;
  right_ast?: Record<string, unknown>;
  tolerance?: number;
  on_ng?: 'block' | 'warn';
  require_reason_on_ng?: boolean;
};

export const saveDailyReportReconciliationRule = (body: DailyReportReconciliationRuleUpsertBody) =>
  apiFetch<{ rule_id: string }>('/daily-reports/config/reconciliation-rules', { method: 'PUT', body: JSON.stringify(body) });

// -----------------------------------------------------------------------------
// P-8 日報設定 — 可視性クラス (§14-5-5)
// -----------------------------------------------------------------------------

export const DAILY_REPORT_VISIBILITY_CONTEXTS = ['author', 'node_manager', 'viewer'] as const;
export type DailyReportVisibilityContext = (typeof DAILY_REPORT_VISIBILITY_CONTEXTS)[number];

export type DailyReportVisibilityClass = {
  class_id: string;
  code: string;
  label: string;
  description: string | null;
  sort_order: number;
  status: 'active' | 'archived';
  updated_at: string;
};

export const fetchDailyReportVisibilityClasses = () =>
  apiFetch<{ classes: DailyReportVisibilityClass[] }>('/daily-reports/config/visibility-classes').then((r) => r.classes);

export type DailyReportVisibilityClassUpsertBody = {
  class_id?: string;
  code?: string;
  label?: string;
  description?: string | null;
  sort_order?: number;
  status?: 'active' | 'archived';
};

export const saveDailyReportVisibilityClass = (body: DailyReportVisibilityClassUpsertBody) =>
  apiFetch<{ class_id: string }>('/daily-reports/config/visibility-classes', { method: 'PUT', body: JSON.stringify(body) });

export const deleteDailyReportVisibilityClass = (classId: string) =>
  apiFetch<{ class_id: string }>(`/daily-reports/config/visibility-classes/${classId}`, { method: 'DELETE' });

export type DailyReportVisibilityRule = {
  rule_id: string;
  class_id: string;
  context: DailyReportVisibilityContext;
  display_level: DailyReportDisplayLevel;
};

export const fetchDailyReportVisibilityRules = () =>
  apiFetch<{ rules: DailyReportVisibilityRule[] }>('/daily-reports/config/visibility-rules').then((r) => r.rules);

/** ⚠️ 3軸すべてを毎回まとめて送る (§14-5-5、設定し忘れた軸が fail-closed になることを防ぐ)。 */
export const saveDailyReportVisibilityRules = (body: {
  class_id: string;
  rules: { context: DailyReportVisibilityContext; display_level: DailyReportDisplayLevel }[];
}) =>
  apiFetch<{ class_id: string }>('/daily-reports/config/visibility-rules', { method: 'PUT', body: JSON.stringify(body) });

/** field_options_json (value_kind='line_items') のクライアント側パース (バックエンド
 *  lib/daily-report/line-items.ts parseDailyReportLineItemFieldOptions のミラー、表示専用)。 */
export type DailyReportLineItemColumn = { key: string; label: string; type: 'text' | 'number' | 'date'; required?: boolean };
export type DailyReportLineItemFieldOptions =
  | { line_item_kind: 'denomination' }
  | { line_item_kind: 'general'; columns: DailyReportLineItemColumn[]; amount_column_key?: string };

export const parseLineItemFieldOptions = (json: string | null): DailyReportLineItemFieldOptions | null => {
  if (!json) return null;
  try {
    const raw = JSON.parse(json) as Record<string, unknown>;
    if (raw.line_item_kind === 'denomination') return { line_item_kind: 'denomination' };
    if (raw.line_item_kind === 'general' && Array.isArray(raw.columns)) {
      return {
        line_item_kind: 'general',
        columns: raw.columns as DailyReportLineItemColumn[],
        amount_column_key: typeof raw.amount_column_key === 'string' ? raw.amount_column_key : undefined,
      };
    }
  } catch {
    // 壊れた設定は「general/denominationいずれでもない」= null 扱い (呼び出し側でフォールバック表示)。
  }
  return null;
};

// =============================================================================
// P-4 添付・OCR確認 (§14-3-B)
// =============================================================================

export const DAILY_REPORT_ATTACHMENT_KINDS = [
  'journal_timeslot', 'journal_menu', 'handwritten_report', 'delivery_slip', 'cash_count', 'other',
] as const;
export type DailyReportAttachmentKind = (typeof DAILY_REPORT_ATTACHMENT_KINDS)[number];

/**
 * ⚠️ 既知の設計ギャップ: バックエンド (migrations/0151_daily_report.sql 冒頭コメント) は
 * 「D分類(証跡)は daily_report_field_catalog にカタログ化しない、attachment_kind で表現する」
 * と決めているが、value_kind='attachment' のテナント項目 (field_definition_id) から
 * attachment_kind への変換手段が正式には定義されていない。本セッションでは
 * `field_options_json` に `{"attachment_kind": "..."}` を持たせる緩い規約 (line_items 項目の
 * `field_options_json` と同じ拡張ポイント) を採用し、未設定時は 'other' にフォールバックする。
 * P-5 (項目定義設定画面) 実装時は、この規約に沿った入力UIを設けること。
 */
export const inferAttachmentKind = (fieldOptionsJson: string | null): DailyReportAttachmentKind => {
  if (fieldOptionsJson) {
    try {
      const raw = JSON.parse(fieldOptionsJson) as Record<string, unknown>;
      if (typeof raw.attachment_kind === 'string' && (DAILY_REPORT_ATTACHMENT_KINDS as readonly string[]).includes(raw.attachment_kind)) {
        return raw.attachment_kind as DailyReportAttachmentKind;
      }
    } catch {
      // 不正なJSONは無視して既定値へ
    }
  }
  return 'other';
};

export const MIN_DAILY_REPORT_ATTACHMENT_REQUIRED_COUNT = 1;
export const MAX_DAILY_REPORT_ATTACHMENT_REQUIRED_COUNT = 10;

/** 2026-09-09 (user Q3): 添付の必要 (最低) 枚数。サーバー側 lib/daily-report/attachments.ts
 *  `parseDailyReportAttachmentRequiredCount` のミラー (表示・P-5編集専用、既定1)。 */
export const inferAttachmentRequiredCount = (fieldOptionsJson: string | null): number => {
  if (fieldOptionsJson) {
    try {
      const raw = JSON.parse(fieldOptionsJson) as Record<string, unknown>;
      const n = raw.required_count;
      if (typeof n === 'number' && Number.isInteger(n)
        && n >= MIN_DAILY_REPORT_ATTACHMENT_REQUIRED_COUNT && n <= MAX_DAILY_REPORT_ATTACHMENT_REQUIRED_COUNT) {
        return n;
      }
    } catch {
      // 不正なJSONは既定値へ
    }
  }
  return MIN_DAILY_REPORT_ATTACHMENT_REQUIRED_COUNT;
};

export type DailyReportAttachmentFull = DailyReportAttachment & {
  file_name: string; mime_type: string; byte_size: number; uploaded_by: string; uploaded_at: string;
};

/** ⚠️ GET /:id/attachments はバックエンド (routes/daily-reports.ts) が `{ attachments: [...] }`
 *  でラップして返す (GET /:id 単票の `attachments` は配列そのものと非対称、実測して判明)。 */
export const fetchDailyReportAttachments = async (dailyReportId: string): Promise<DailyReportAttachment[]> => {
  const res = await apiFetch<{ attachments: DailyReportAttachment[] }>(`/daily-reports/${dailyReportId}/attachments`);
  return res.attachments;
};

/** apiFetch は Content-Type を application/json に固定するため multipart には使えない
 *  (AiOcrPage.tsx の authFetch と同じ理由・同じパターン)。§14-3-B「中止導線」のため
 *  AbortSignal を受け付ける。 */
const multipartFetch = async <T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST', body: form, headers: token ? { Authorization: `Bearer ${token}` } : {}, signal,
  });
  handleUnauthorized(res.status);
  const body = await res.json();
  if (!res.ok || !body.success) {
    throw new ApiClientError(res.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? 'エラー', body.error?.details);
  }
  return body.data as T;
};

export const uploadDailyReportAttachment = (
  dailyReportId: string, fieldDefinitionId: string, attachmentKind: DailyReportAttachmentKind, file: File, signal?: AbortSignal,
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('attachment_kind', attachmentKind);
  form.append('field_definition_id', fieldDefinitionId);
  return multipartFetch<DailyReportAttachmentFull>(`/daily-reports/${dailyReportId}/attachments`, form, signal);
};

export const reocrDailyReportAttachment = (dailyReportId: string, attachmentId: string, file: File, signal?: AbortSignal) => {
  const form = new FormData();
  form.append('file', file);
  return multipartFetch<DailyReportOcrView>(`/daily-reports/${dailyReportId}/attachments/${attachmentId}/reocr`, form, signal);
};

export const confirmDailyReportOcr = (dailyReportId: string, attachmentId: string, reason?: string) =>
  apiFetch<DailyReportOcrView>(`/daily-reports/${dailyReportId}/attachments/${attachmentId}/confirm-ocr`, {
    method: 'POST', body: JSON.stringify(reason ? { reason } : {}),
  });

export const deleteDailyReportAttachment = (dailyReportId: string, attachmentId: string) =>
  apiFetch<{ attachment_id: string }>(`/daily-reports/${dailyReportId}/attachments/${attachmentId}`, { method: 'DELETE' });

/** 添付画像バイトを取得する (`AiOcrReviewPage.tsx` の `PageImage` と同じ fetch+blob パターン)。
 *  呼び出し元が `URL.createObjectURL`/`revokeObjectURL` を管理すること。 */
export const fetchDailyReportAttachmentBlob = async (dailyReportId: string, attachmentId: string): Promise<Blob> => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}/daily-reports/${dailyReportId}/attachments/${attachmentId}/content`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  handleUnauthorized(res.status);
  if (!res.ok) throw new ApiClientError(res.status, 'FETCH_FAILED', '画像の取得に失敗しました');
  return res.blob();
};
